import React from 'react';
import { ISelectOption, ISpruceFieldProps, Select, SpruceField } from 'ui-toolkit';
import { FormikContextType } from 'formik';
import { IFetchDataState } from '../../../../store/IFetchDataState';
import { PreExistingContactSelect } from '../subComponents/PreExistingContactSelect';
import { IClientUserData } from '../../../../types/clients';
import { activeClientContactsFunctionsOptions } from '../../../../types/contacts/activeClientContactsFunctionsOptions';
import { useListContactFunctionsQuery } from '../../../../API/contacts/ContactFunctionsApi';
import { getContactFunctionValue } from '../../../../utilities/contact/getContactFunctionValue';
import { OrderContactNotesField } from '../subComponents/OrderContactNotesField';
import { ErrorBoundaryConstellation } from '../../../../constellationExtended/ErrorBoundary/ErrorBoundaryConstellation';

interface AddPreExistingClientOrderContactFormProps {
  preExistingClientContacts: IFetchDataState<string, IClientUserData[]>;
  onSelectContact: (contactId: string) => void;
}

export const AddPreExistingClientOrderContactForm: React.FC<AddPreExistingClientOrderContactFormProps> = ({
  preExistingClientContacts,
  onSelectContact,
}) => {
  const isFetching = preExistingClientContacts.fetchStatus.fetching;
  const { data: contactFunctions = [] } = useListContactFunctionsQuery();

  const clientsOptions: ISelectOption[] = preExistingClientContacts.data.map((contact) => ({
    label: `${contact.firstName} ${contact.lastName}`,
    value: contact.id,
  }));

  const handleChange = (formik: FormikContextType<any>, option?: ISelectOption): void => {
    if (!!option) {
      const contact = clientContactFromId(option.value);
      onSelectContact(option.value);
      if (!!contact) {
        // Use setValues instead of multiple setFieldValue calls for better performance and proper dirty flag update
        formik.setValues({
          ...formik.values,
          id: contact.id,
          companyName: contact.companyName,
          email: contact.email,
          firstName: contact.firstName,
          lastName: contact.lastName,
          functions: contact.functions,
        });
      }
    } else {
      // Clear all fields
      formik.setValues({
        ...formik.values,
        id: '',
        companyName: '',
        email: '',
        firstName: '',
        lastName: '',
        functions: [],
      });
    }
  };
  const clientContactFromId = (contactId: string): IClientUserData | undefined =>
    preExistingClientContacts.data.find((contact) => contact.id === contactId);

  return (
    <ErrorBoundaryConstellation>
      <PreExistingContactSelect
        isFetching={isFetching}
        options={clientsOptions}
        handleChange={handleChange}
        label={'An existing client contact'}
      />
      <SpruceField name={'functions'}>
        {({ field }: ISpruceFieldProps) => (
          <Select
            {...field}
            onChange={(values: ISelectOption[]) => field.onChange((values ?? []).map((value) => value.value))}
            value={getContactFunctionValue(field.value, contactFunctions)}
            options={activeClientContactsFunctionsOptions(contactFunctions)}
            isClearable={true}
            isMulti={true}
            label={'Function(s)'}
            isRequired={true}
          />
        )}
      </SpruceField>
      <OrderContactNotesField name={'notes'} />
    </ErrorBoundaryConstellation>
  );
};
